import { useUser } from '@store/user';
import type { StepId } from '@store/user/interfaces/userInterfaces';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface StepGuardProps {
  children: ReactNode;
  requiredStep: StepId;
}

// Define the step order mapping (excluding finish which bypasses StepGuard)
const STEP_ORDER: Partial<Record<StepId, number>> = {
  validation: 1,
  personal: 2,
  residence: 3,
  finance: 4,
  job: 5,
  references: 6,
  confirm: 7,
  // finish: bypasses StepGuard entirely
};

export function StepGuard({ children, requiredStep }: StepGuardProps) {
  const { user } = useUser();
  const location = useLocation();

  // Get the current step order
  const currentStepOrder = STEP_ORDER[requiredStep];

  if (!currentStepOrder) {
    // If step is not found, allow access (shouldn't happen)
    return <>{children}</>;
  }

  // If trying to access validation and it's already completed - /personal
  if (requiredStep === 'validation' && user.steps.validation?.isCompleted) {
    return <Navigate to="/personal" replace />;
  }

  // If NOT validation and validation NOT completed - redirect to /validation
  if (requiredStep !== 'validation' && !user.steps.validation?.isCompleted) {
    return <Navigate to="/validation" replace />;
  }

  // Validation completed - allow direct navigation
  // If validation is completed and the user is navigating explicitly,
  if (user.steps.validation?.isCompleted) {
    return <>{children}</>;
  }

  // Validation not completed - apply strict guards
  const canAccessStep = (): boolean => {
    if (currentStepOrder === 1) return true;

    for (let order = 1; order < currentStepOrder; order++) {
      const stepId = (Object.keys(STEP_ORDER) as StepId[]).find(
        key => STEP_ORDER[key] === order,
      );

      if (stepId && !user.steps[stepId]?.isCompleted) {
        return false;
      }
    }

    return true;
  };

  // Find the first incomplete step to redirect to
  const getRedirectStep = (): string => {
    if (!user.steps.validation?.isCompleted) {
      return '/validation';
    }

    for (let order = 1; order <= 7; order++) {
      const stepId = (Object.keys(STEP_ORDER) as StepId[]).find(
        key => STEP_ORDER[key] === order,
      );

      if (stepId && !user.steps[stepId]?.isCompleted) {
        return user.steps[stepId]?.route || '/validation';
      }
    }

    // If all steps are completed, allow access to any step
    return location.pathname;
  };

  if (!canAccessStep()) {
    const redirectTo = getRedirectStep();
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
